/**
 * TranscriptionConfigSection - Transcrição Automática de mídias recebidas
 *
 * Duas flags por canal (áudios e vídeos recebidos). Canal sem o campo
 * persistido = tudo habilitado (default day-0). A partir do primeiro save,
 * o backend respeita a configuração. A transcrição sob demanda (botão no
 * chat) não é afetada por estas flags.
 */

import React from 'react';
import type { UpdateChannelRequest, ChannelTranscriptionConfig } from '@troia-v3/types';
import type { ChannelResponse } from '@/modules/channels/types';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Switch } from '@/shared/components/ui/Switch';
import { Icon } from '@/shared/icons';

interface TranscriptionConfigSectionProps {
  channel: ChannelResponse;
  formData: Partial<UpdateChannelRequest>;
  onFieldChange: (field: keyof UpdateChannelRequest, value: any) => void;
}

export const TranscriptionConfigSection: React.FC<TranscriptionConfigSectionProps> = ({
  channel,
  formData,
  onFieldChange,
}) => {
  // Ausência do campo = tudo habilitado (comportamento day-0)
  const currentConfig: ChannelTranscriptionConfig = formData.transcriptionConfig ||
    channel.transcriptionConfig ||
    { audio: true, video: true };

  const handleToggle = (key: keyof ChannelTranscriptionConfig) => (checked: boolean) => {
    // Sempre envia o objeto completo — o PUT genérico substitui o sub-objeto inteiro
    onFieldChange('transcriptionConfig', { ...currentConfig, [key]: checked });
  };

  return (
    <Card data-testid="transcription-config-section">
      <CardHeader className="pb-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="mic" className="w-4 h-4" style={{ color: 'var(--theme-button-primary)' }} />
          <h3 className="font-bold text-[15px]" style={{ color: 'var(--theme-card-text)' }}>
            Transcrição Automática
          </h3>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-4">
        <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
          Transcreve automaticamente o conteúdo falado das mídias recebidas neste canal.
          O texto fica disponível na conversa e para os agentes de IA.
        </p>

        {/* Áudios recebidos */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <label
              htmlFor="transcription-audio-toggle"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Áudios recebidos
            </label>
            <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
              Transcrever automaticamente mensagens de áudio ao recebê-las
            </p>
          </div>
          <Switch
            id="transcription-audio-toggle"
            checked={currentConfig.audio}
            onChange={handleToggle('audio')}
            data-testid="transcription-audio-toggle"
          />
        </div>

        {/* Vídeos recebidos */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <label
              htmlFor="transcription-video-toggle"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Vídeos recebidos
            </label>
            <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
              Transcrever automaticamente o áudio de vídeos ao recebê-los
            </p>
          </div>
          <Switch
            id="transcription-video-toggle"
            checked={currentConfig.video}
            onChange={handleToggle('video')}
            data-testid="transcription-video-toggle"
          />
        </div>

        {/* Informação quando tudo desligado */}
        {!currentConfig.audio && !currentConfig.video && (
          <div
            className="p-3 rounded-lg border"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
              Nenhuma mídia será transcrita automaticamente. Ainda é possível transcrever
              mensagens individualmente pelo botão de transcrição no chat.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

TranscriptionConfigSection.displayName = 'TranscriptionConfigSection';
